import { LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { config } from '@/config';
import { OAuthProvider, useOAuthProviders } from '@/hooks/useOAuthProviders';

interface OAuthButtonsProps {
  /** When set, the flow joins this invitation's org/team instead of creating one. */
  invitationToken?: string;
  /**
   * Org whose own (non-global) providers should be offered alongside the
   * globals. Only set this where the identity is already known — re-auth — not
   * on the cold sign-in page, which has nobody to scope to.
   */
  organizationId?: string | null;
  /** Frontend path to land on after a successful login (default /home). */
  returnTo?: string;
  /** Render an "or" separator above the buttons (only when providers exist). */
  withDivider?: boolean;
}

/** Google's brand "G" mark, per their sign-in branding guidelines. */
function GoogleLogo() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58Z"
      />
    </svg>
  );
}

/** Per-provider visual treatment keyed on the stable backend slug. */
function providerBranding(slug: string): { icon: React.ReactNode; className: string } {
  switch (slug) {
    case 'google':
      return {
        icon: <GoogleLogo />,
        className: 'bg-white text-[#1f1f1f] border-[#747775] hover:bg-[#f7f8f8]',
      };
    default:
      return { icon: <LogIn className="h-4 w-4" />, className: '' };
  }
}

/**
 * Renders one "Continue with <provider>" button per enabled OAuth/OIDC provider.
 *
 * The flow is a server-side Authorization-Code redirect, so each button is a
 * plain full-page navigation to the backend `/start` endpoint — NOT an XHR. The
 * backend handles the IdP round-trip, sets the session cookie, and redirects
 * back to `returnTo`. Known providers (by slug) get branded styling.
 */
export function OAuthButtons({
  invitationToken,
  organizationId,
  returnTo = '/home',
  withDivider = false,
}: OAuthButtonsProps) {
  const { data: providers } = useOAuthProviders({ invitationToken, organizationId });

  if (!providers || providers.length === 0) {
    return null;
  }

  const startUrl = (providerId: string): string => {
    const params = new URLSearchParams({ return_to: returnTo });
    if (invitationToken) {
      params.set('invitation_token', invitationToken);
    }
    return `${config.backendApi}/api/auth/oauth/${providerId}/start?${params.toString()}`;
  };

  return (
    <div className="flex flex-col gap-2">
      {withDivider ? (
        <div className="flex items-center gap-3 mb-2">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground uppercase tracking-wider font-mono">or</span>
          <div className="flex-1 h-px bg-border" />
        </div>
      ) : null}
      {providers.map((provider: OAuthProvider) => {
        const branding = providerBranding(provider.slug);
        return (
          <Button
            key={provider.id}
            type="button"
            variant="outline"
            className={cn('w-full', branding.className)}
            onClick={() => {
              window.location.assign(startUrl(provider.id));
            }}
          >
            {branding.icon}
            Continue with {provider.displayName}
          </Button>
        );
      })}
    </div>
  );
}
